import { describeUnplayable } from '@cardengine/shared';
import type {
  BotDifficulty,
  ChatMessage,
  FreecellGameEvent,
  FreecellMoveDest,
  FreecellMoveSource,
  FreecellRules,
  FreecellView,
  GameEvent,
  GameType,
  LobbyView,
  MinesweeperRules,
  MinesweeperView,
  MinesweeperGameEvent,
  NoughtsGameEvent,
  NoughtsRules,
  NoughtsView,
  SessionInfo,
  SolitaireGameEvent,
  SolitaireMoveDest,
  SolitaireMoveSource,
  SolitaireRules,
  SolitaireView,
  SpiderGameEvent,
  SpiderMoveDest,
  SpiderMoveSource,
  SpiderRules,
  SpiderView,
  Suit,
  SwitchRules,
  SwitchView,
} from '@cardengine/shared';
import { minesweeper } from '@cardengine/shared';
import { create } from 'zustand';
import { findCardBack } from './cardBacks.js';
import {
  narrate,
  narrateFreecell,
  narrateMinesweeper,
  narrateNoughts,
  narrateSolitaire,
  narrateSpider,
  type EventTone,
  type NarratedEvent,
} from './eventText.js';
import { navigate, TABLE_PATH } from './router.js';
import { request, socket } from './socket.js';
import { fanfare, pickUpSound, playCardSound, turnChime, unlockAudio } from './sound.js';

/*
 * Minesweeper's square encoding, pulled out of the namespace once. The store
 * reads it in three places and the board in several more; spelling
 * `minesweeper.HIDDEN` at each of them buries the one thing those lines are
 * actually saying.
 */
const { HIDDEN: MS_HIDDEN, FLAGGED: MS_FLAGGED } = minesweeper;

/**
 * Whether a chord off this square would do anything — asked of the view, not
 * the state.
 *
 * The engine has its own `canChord`, and this is not a second implementation of
 * the rule so much as the same rule asked of the only thing the client has. It
 * can be, because the numbers are the point: an open square carries how many
 * mines touch it, the client can see its own flags, and that is the entire
 * test. Nothing here needs to know where a mine is, which is the same reason
 * the view sends no list of legal moves.
 */
function canChordAt(view: MinesweeperView, cell: number): boolean {
  const number = view.cells[cell];
  if (number === undefined || number <= 0) return false;

  const row = Math.floor(cell / view.width);
  const column = cell % view.width;
  let flagged = 0;
  let closed = 0;

  for (let dRow = -1; dRow <= 1; dRow++) {
    for (let dColumn = -1; dColumn <= 1; dColumn++) {
      if (dRow === 0 && dColumn === 0) continue;
      const r = row + dRow;
      const c = column + dColumn;
      if (r < 0 || c < 0 || r >= view.height || c >= view.width) continue;

      const neighbour = view.cells[r * view.width + c];
      if (neighbour === MS_FLAGGED) flagged++;
      else if (neighbour === MS_HIDDEN) closed++;
    }
  }

  return flagged === number && closed > 0;
}

const SESSION_KEY = 'cardengine:session';
const USERNAME_KEY = 'cardengine:username';
const HIGHLIGHT_KEY = 'cardengine:highlightPlayable';
const CARD_BACK_KEY = 'cardengine:cardBack';
const SOUND_KEY = 'cardengine:sound';

function loadCardBack(): string {
  try {
    return findCardBack(localStorage.getItem(CARD_BACK_KEY)).id;
  } catch {
    return findCardBack(null).id;
  }
}

function loadSound(): boolean {
  try {
    // Absent means on. A card game with no sound at all feels broken, and it
    // is one click to silence in the lobby.
    return localStorage.getItem(SOUND_KEY) !== '0';
  } catch {
    return true;
  }
}

function loadHighlight(): boolean {
  try {
    // Absent means off — the assistance is opt-in.
    return localStorage.getItem(HIGHLIGHT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Any game's ruleset. The host sets rules for the one game their lobby is
 * running, so the callers that pass these never mix two together — spelling
 * the union out once beats repeating it at every signature.
 */
export type AnyRules =
  | SwitchRules
  | SolitaireRules
  | FreecellRules
  | SpiderRules
  | NoughtsRules
  | MinesweeperRules;

export type Screen = 'home' | 'lobby' | 'game';
export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'offline';

export interface Toast {
  id: number;
  text: string;
  tone: 'info' | 'success' | 'warn' | 'error';
}

export interface LogEntry {
  id: number;
  text: string;
  tone: EventTone;
  turn: number;
}

interface AppState {
  connection: ConnectionState;
  screen: Screen;
  session: SessionInfo | null;
  lobby: LobbyView | null;
  /**
   * Each game's board sits in its own slot rather than one shared union. Every
   * Switch component already reads `game`, and a tagged union would make each
   * of them narrow before it could touch a field it has always had. The tag
   * arrives on the wire and is spent on receipt, in one place.
   */
  game: SwitchView | null;
  solitaire: SolitaireView | null;
  freecell: FreecellView | null;
  spider: SpiderView | null;
  noughts: NoughtsView | null;
  minesweeper: MinesweeperView | null;
  chat: ChatMessage[];
  toasts: Toast[];
  /** Most recent engine events, consumed by the table for animation cues. */
  lastEvents: GameEvent[];
  /**
   * The same, for Spider. Its board animates two things the view alone cannot
   * describe — which column a finished run came off, and in what suit — and
   * both are only in the event.
   */
  spiderEvents: SpiderGameEvent[];
  /** Running narration of the round, so nothing is missed when play is quick. */
  log: LogEntry[];
  username: string;
  busy: boolean;
  homeError: string | null;

  selectedCardIds: string[];
  /** The next play will announce "last card". Armed automatically at two cards. */
  /** The player has overridden the automatic choice, so don't re-arm it. */
  /**
   * Whether the side panel is showing. When it is, the log is already narrating
   * every action, so duplicating it as toasts just covers the table.
   */
  logPanelOpen: boolean;
  setLogPanelOpen: (open: boolean) => void;
  /**
   * Ring the cards you are allowed to play and grey out the rest. Off by
   * default: working out what is legal is part of playing the game.
   */
  highlightPlayable: boolean;
  setHighlightPlayable: (on: boolean) => void;
  /** Which card-back colourway this player sees. Personal, not a house rule. */
  cardBack: string;
  setCardBack: (id: string) => void;
  /** Card sounds and the your-turn chime. Personal, like the card backs. */
  sound: boolean;
  setSound: (on: boolean) => void;
  /**
   * Why the last attempted card was refused. Shown inline beside your hand
   * rather than as a floating toast — it is feedback about your own cards, and
   * as a toast it sat on top of the other players.
   */
  rejection: { id: number; text: string } | null;
  showRejection: (text: string) => void;

  connect: () => void;
  createLobby: (username: string, rules?: Partial<AnyRules>, gameType?: GameType) => Promise<boolean>;
  /** Turn the stock over, or flip the waste back when it is spent. */
  solitaireDraw: () => void;
  solitaireMove: (from: SolitaireMoveSource, to: SolitaireMoveDest) => void;
  solitaireAutoComplete: () => void;
  freecellMove: (from: FreecellMoveSource, to: FreecellMoveDest) => void;
  freecellAutoComplete: () => void;
  spiderMove: (from: SpiderMoveSource, to: SpiderMoveDest) => void;
  /** Turn one card face up onto every column. */
  spiderDeal: () => void;
  /** Claim a square. The server fills in whose mark it is from the session. */
  noughtsMark: (cell: number) => void;
  /** Open a square. The first one lays the mines, around it. */
  minesweeperReveal: (cell: number) => void;
  /** Mark or unmark a square. Toggles. */
  minesweeperFlag: (cell: number) => void;
  /** Open everything round an already-open number whose flags add up. */
  minesweeperChord: (cell: number) => void;
  /**
   * Whether a plain press flags instead of opening. A right-click always
   * flags, but there is no right button on a phone and no obvious one on a
   * keyboard, so the board carries a switch and this is where it lives.
   */
  flagMode: boolean;
  setFlagMode: (on: boolean) => void;
  joinLobby: (username: string, code: string) => Promise<boolean>;
  leaveLobby: () => Promise<void>;
  updateRules: (rules: Partial<AnyRules>) => void;
  addBot: (difficulty: BotDifficulty) => void;
  removeMember: (playerId: string) => void;
  regenerateCode: () => void;
  startGame: () => void;
  rematch: () => void;
  playAgain: () => void;

  playSelected: () => void;
  playCards: (cardIds: string[]) => void;
  drawCard: () => void;
  passTurn: () => void;
  chooseSuit: (suit: Suit) => void;
  sendChat: (text: string) => void;

  toggleCard: (cardId: string) => void;
  clearSelection: () => void;
  pushToast: (text: string, tone?: Toast['tone']) => void;
  dismissToast: (id: number) => void;
  setHomeError: (message: string | null) => void;
}

let toastSeq = 0;
let logSeq = 0;
let rejectionSeq = 0;
/** Distinguishes a page load from a later socket reconnect. */
let hasConnectedBefore = false;

function loadSession(): SessionInfo | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as SessionInfo) : null;
  } catch {
    return null;
  }
}

function saveSession(session: SessionInfo | null): void {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // Private browsing with storage disabled: sessions just won't survive a reload.
  }
}

function loadUsername(): string {
  try {
    return localStorage.getItem(USERNAME_KEY) ?? '';
  } catch {
    return '';
  }
}

export const useStore = create<AppState>((set, get) => ({
  connection: 'idle',
  screen: 'home',
  session: null,
  lobby: null,
  game: null,
  chat: [],
  toasts: [],
  solitaire: null,
  freecell: null,
  spider: null,
  noughts: null,
  minesweeper: null,
  lastEvents: [],
  spiderEvents: [],
  log: [],
  username: loadUsername(),
  busy: false,
  homeError: null,
  selectedCardIds: [],
  logPanelOpen: false,
  setLogPanelOpen: (open) => set({ logPanelOpen: open }),
  highlightPlayable: loadHighlight(),
  setHighlightPlayable: (on) => {
    try {
      localStorage.setItem(HIGHLIGHT_KEY, on ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
    set({ highlightPlayable: on });
  },
  cardBack: loadCardBack(),
  setCardBack: (id) => {
    const back = findCardBack(id);
    try {
      localStorage.setItem(CARD_BACK_KEY, back.id);
    } catch {
      /* storage unavailable */
    }
    set({ cardBack: back.id });
  },
  sound: loadSound(),
  setSound: (on) => {
    try {
      localStorage.setItem(SOUND_KEY, on ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
    // Turning it on is itself a gesture, which is exactly when a browser will
    // let us start audio — so take the chance to play the confirming chime.
    if (on) {
      unlockAudio();
      turnChime();
    }
    set({ sound: on });
  },
  flagMode: false,
  setFlagMode: (on) => set({ flagMode: on }),
  rejection: null,
  showRejection: (text) => {
    const id = ++rejectionSeq;
    set({ rejection: { id, text } });
    setTimeout(() => {
      if (get().rejection?.id === id) set({ rejection: null });
    }, 3_500);
  },

  connect: () => {
    if (get().connection !== 'idle') return;
    set({ connection: 'connecting' });
    registerHandlers(set, get);
    socket.connect();
  },

  createLobby: async (username, rules, gameType) => {
    set({ busy: true, homeError: null });
    const result = await request<SessionInfo>('lobby:create', { username, rules, gameType });
    set({ busy: false });

    if (!result.ok) {
      set({ homeError: result.error.message });
      return false;
    }
    try {
      localStorage.setItem(USERNAME_KEY, username);
    } catch {
      /* storage unavailable */
    }
    saveSession(result.data);
    set({ session: result.data, username, screen: 'lobby', chat: [] });
    navigate(TABLE_PATH);
    return true;
  },

  joinLobby: async (username, code) => {
    set({ busy: true, homeError: null });
    const result = await request<SessionInfo>('lobby:join', { username, code });
    set({ busy: false });

    if (!result.ok) {
      set({ homeError: result.error.message });
      return false;
    }
    try {
      localStorage.setItem(USERNAME_KEY, username);
    } catch {
      /* storage unavailable */
    }
    saveSession(result.data);
    set({ session: result.data, username, screen: 'lobby', chat: [] });
    navigate(TABLE_PATH);
    return true;
  },

  leaveLobby: async () => {
    await request('lobby:leave');
    saveSession(null);
    set({
      session: null,
      lobby: null,
      game: null,
      // Every board slot, not just the Switch one — leaving a table must not
      // leave the last position sitting in the store for the next game to
      // flash up before its own first view arrives.
      solitaire: null,
      freecell: null,
      spider: null,
      noughts: null,
      minesweeper: null,
      chat: [],
      log: [],
      screen: 'home',
      selectedCardIds: [],
        });
    navigate('/');
  },

  updateRules: (rules) => {
    void request('lobby:updateRules', rules);
  },

  addBot: (difficulty) => {
    void withError(get, request('lobby:addBot', { difficulty }));
  },

  removeMember: (playerId) => {
    void withError(get, request('lobby:removeMember', playerId));
  },

  regenerateCode: () => {
    void withError(get, request('lobby:newCode'));
  },

  startGame: () => {
    void withError(get, request('lobby:start'));
  },

  rematch: () => {
    set({ selectedCardIds: [] });
    void withError(get, request('lobby:rematch'));
  },

  playAgain: () => {
    set({ selectedCardIds: [], rejection: null });
    void withError(get, request('lobby:playAgain'));
  },

  playSelected: () => {
    const { selectedCardIds } = get();
    if (selectedCardIds.length > 0) get().playCards(selectedCardIds);
  },

  playCards: (cardIds) => {
    const { session, game } = get();
    if (!session || cardIds.length === 0) return;

    // With highlighting off, players will try illegal cards on purpose. Answer
    // straight away with the reason instead of bouncing off the server.
    if (game) {
      if (game.currentPlayerId !== game.yourPlayerId) {
        get().showRejection('It is not your turn.');
        set({ selectedCardIds: [] });
        return;
      }
      const lead = game.yourHand.find((c) => c.id === cardIds[0]);
      const isLegalLead = game.yourLegalMoves.some(
        (m) => m.kind === 'play' && m.cardIds[0] === cardIds[0],
      );
      if (lead && !isLegalLead) {
        get().showRejection(describeUnplayable(game, lead));
        set({ selectedCardIds: [] });
        return;
      }
    }

    set({ selectedCardIds: [] });
    void withError(
      get,
      request('game:action', {
        type: 'play',
        playerId: session.playerId,
        cardIds,
      }),
    );
  },

  drawCard: () => {
    const { session } = get();
    if (!session) return;
    set({ selectedCardIds: [] });
    void withError(get, request('game:action', { type: 'draw', playerId: session.playerId }));
  },

  passTurn: () => {
    const { session } = get();
    if (!session) return;
    set({ selectedCardIds: [] });
    void withError(get, request('game:action', { type: 'pass', playerId: session.playerId }));
  },

  chooseSuit: (suit) => {
    const { session } = get();
    if (!session) return;
    void withError(get, request('game:action', { type: 'chooseSuit', playerId: session.playerId, suit }));
  },


  /*
   * Solitaire actions carry no player id — there is only one seat, and the
   * server fills it in from the session rather than trusting the client.
   */
  solitaireDraw: () => {
    const view = get().solitaire;
    if (!view) return;
    // One button serves both: when the stock is spent it turns the waste back.
    const action = view.stockCount > 0 ? { type: 'draw' as const } : { type: 'recycle' as const };
    void withError(get, request('game:action', action));
  },

  solitaireMove: (from, to) => {
    void withError(get, request('game:action', { type: 'move', from, to }));
  },

  solitaireAutoComplete: () => {
    void withError(get, request('game:action', { type: 'autoComplete' }));
  },

  /* FreeCell's are the same two shapes, and carry no player id for the same
   * reason: one seat, filled in by the server from the session. */
  freecellMove: (from, to) => {
    void withError(get, request('game:action', { type: 'move', from, to }));
  },

  freecellAutoComplete: () => {
    void withError(get, request('game:action', { type: 'autoComplete' }));
  },

  /* And Spider's, which has no cells to park in and no auto-finish — just
   * moves, and the stock. */
  spiderMove: (from, to) => {
    void withError(get, request('game:action', { type: 'move', from, to }));
  },

  spiderDeal: () => {
    void withError(get, request('game:action', { type: 'deal' }));
  },

  /*
   * Noughts and crosses has an opponent, so unlike the patience games there is
   * a turn to be out of. The board only offers squares it was given legal moves
   * for, so this answers locally rather than making the server say no — the
   * same courtesy `playCards` extends to a card you cannot play.
   */
  noughtsMark: (cell) => {
    const view = get().noughts;
    if (!view) return;

    if (view.currentPlayerId !== view.yourPlayerId) {
      get().showRejection('It is not your turn.');
      return;
    }
    if (!view.yourLegalMoves.some((move) => move.cell === cell)) {
      get().showRejection('That square is taken.');
      return;
    }

    void withError(get, request('game:action', { type: 'mark', cell }));
  },

  /*
   * Minesweeper's three actions, carrying no player id for the reason the
   * patience games' do not: one seat, filled in by the server from the session.
   *
   * Each answers locally before it asks, the way `playCards` does for a card
   * you cannot play. It matters more here than anywhere else on the site,
   * because a Minesweeper board is played in bursts — a chord, a flag, three
   * quick squares — and a round trip to be told "that one is already open"
   * would be felt as lag rather than read as an answer.
   */
  minesweeperReveal: (cell) => {
    const view = get().minesweeper;
    if (!view || view.phase !== 'playing') return;

    const square = view.cells[cell];
    if (square === undefined) return;
    if (square >= 0) return;
    if (square === MS_FLAGGED) {
      get().showRejection('Take the flag off first.');
      return;
    }

    void withError(get, request('game:action', { type: 'reveal', cell }));
  },

  minesweeperFlag: (cell) => {
    const view = get().minesweeper;
    if (!view || view.phase !== 'playing') return;

    const square = view.cells[cell];
    if (square === undefined || square >= 0) return;

    if (square === MS_HIDDEN && view.rules.flagsLimited && view.flagsLeft <= 0) {
      get().showRejection('No flags left — take one off somewhere else.');
      return;
    }

    void withError(get, request('game:action', { type: 'flag', cell }));
  },

  minesweeperChord: (cell) => {
    const view = get().minesweeper;
    if (!view || view.phase !== 'playing') return;
    if (!canChordAt(view, cell)) return;

    void withError(get, request('game:action', { type: 'chord', cell }));
  },

  sendChat: (text) => {
    socket.emit('chat:send', text);
  },

  toggleCard: (cardId) => {
    const { game, selectedCardIds } = get();
    if (!game) return;

    if (selectedCardIds.includes(cardId)) {
      set({ selectedCardIds: selectedCardIds.filter((id) => id !== cardId) });
      return;
    }

    const card = game.yourHand.find((c) => c.id === cardId);
    if (!card) return;

    // Picking up a card you cannot play should tell you why, the same way the
    // table would. This matters most with highlighting switched off.
    const isLegalLead = game.yourLegalMoves.some((m) => m.kind === 'play' && m.cardIds[0] === cardId);
    const companionOf = game.yourLegalMoves.find(
      (m) => m.kind === 'play' && m.cardIds[0] === selectedCardIds[0] && m.cardIds.includes(cardId),
    );
    if (!isLegalLead && !companionOf) {
      get().showRejection(
        game.currentPlayerId !== game.yourPlayerId
          ? 'It is not your turn.'
          : describeUnplayable(game, card),
      );
      return;
    }

    // A multi-card play must be all one rank, so switching rank replaces the
    // selection rather than building an illegal pile.
    const currentRank = game.yourHand.find((c) => c.id === selectedCardIds[0])?.rank;
    const next =
      selectedCardIds.length === 0 || currentRank !== card.rank ? [cardId] : [...selectedCardIds, cardId];

    set({ selectedCardIds: next });
  },

  clearSelection: () => set({ selectedCardIds: [] }),

  pushToast: (text, tone = 'info') => {
    const id = ++toastSeq;
    // Capped: a burst of AI turns would otherwise stack a wall of them.
    set((state) => ({ toasts: [...state.toasts.slice(-2), { id, text, tone }] }));
    // Long enough to read a full sentence while bots are moving.
    setTimeout(() => get().dismissToast(id), 6_000);
  },

  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  setHomeError: (message) => set({ homeError: message }),
}));

/** Surfaces a failed request as a toast without the caller having to care. */
async function withError(get: () => AppState, promise: Promise<{ ok: boolean; error?: { message: string } }>) {
  const result = await promise;
  if (!result.ok && result.error) get().pushToast(result.error.message, 'error');
}

type SetState = (partial: Partial<AppState> | ((state: AppState) => Partial<AppState>)) => void;

let handlersRegistered = false;

function registerHandlers(set: SetState, get: () => AppState): void {
  if (handlersRegistered) return;
  handlersRegistered = true;

  socket.on('connect', () => {
    set({ connection: 'connected' });
    const stored = get().session ?? loadSession();
    if (!stored) return;

    // Reclaim the seat we held before the drop.
    const firstConnect = !hasConnectedBefore;
    hasConnectedBefore = true;

    void request<SessionInfo>('session:resume', { sessionId: stored.sessionId }).then((result) => {
      if (result.ok) {
        set({ session: result.data });
        if (firstConnect) navigate(TABLE_PATH, { replace: true });
      } else {
        saveSession(null);
        set({ session: null, lobby: null, game: null, screen: 'home' });
      }
    });
  });

  socket.on('disconnect', () => set({ connection: 'reconnecting' }));
  socket.io.on('reconnect_attempt', () => set({ connection: 'reconnecting' }));
  socket.io.on('error', () => set({ connection: 'offline' }));

  socket.on('lobby:state', (lobby) => {
    const previous = get().lobby;
    set({
      lobby,
      screen: lobby.status === 'waiting' ? 'lobby' : 'game',
    });
    // Returning to the lobby between rounds should not leave stale selections.
    if (previous?.status !== 'waiting' && lobby.status === 'waiting') {
      set({ game: null, selectedCardIds: [] });
    }
  });

  socket.on('game:view', (view) => {
    if (view.gameType === 'solitaire') {
      set({ solitaire: view });
      return;
    }
    if (view.gameType === 'freecell') {
      set({ freecell: view });
      return;
    }
    if (view.gameType === 'spider') {
      set({ spider: view });
      return;
    }
    if (view.gameType === 'noughts-and-crosses') {
      set({ noughts: view });
      return;
    }
    if (view.gameType === 'minesweeper') {
      set({ minesweeper: view });
      return;
    }

    set({ game: view });

    // Drop any selected card that is no longer in hand.
    const held = new Set(view.yourHand.map((c) => c.id));
    const selected = get().selectedCardIds.filter((id) => held.has(id));
    if (selected.length !== get().selectedCardIds.length) set({ selectedCardIds: selected });

  });

  socket.on('game:events', (events) => {
    // A fresh deal starts a fresh log rather than piling onto the last round.
    if (events.some((e) => e.type === 'gameStarted')) set({ log: [] });

    const gameType = get().lobby?.gameType;
    if (gameType === 'solitaire') {
      announceSolo(events as SolitaireGameEvent[], narrateSolitaire, get, set);
      return;
    }
    if (gameType === 'freecell') {
      announceSolo(events as FreecellGameEvent[], narrateFreecell, get, set);
      return;
    }
    if (gameType === 'spider') {
      const spiderEvents = events as SpiderGameEvent[];
      set({ spiderEvents });
      announceSolo(spiderEvents, narrateSpider, get, set);
      return;
    }
    if (gameType === 'noughts-and-crosses') {
      announceNoughts(events as NoughtsGameEvent[], get, set);
      return;
    }
    if (gameType === 'minesweeper') {
      announceMinesweeper(events as MinesweeperGameEvent[], get, set);
      return;
    }

    const switchEvents = events as GameEvent[];
    set({ lastEvents: switchEvents });
    announce(switchEvents, get, set);
  });

  socket.on('chat:message', (message) => {
    set((state) => ({ chat: [...state.chat.slice(-99), message] }));
  });

  socket.on('system:error', (error) => {
    get().pushToast(error.message, 'error');
  });

  socket.on('lobby:closed', (reason) => {
    saveSession(null);
    set({
      session: null,
      lobby: null,
      game: null,
      solitaire: null,
      freecell: null,
      spider: null,
      noughts: null,
      minesweeper: null,
      chat: [],
      screen: 'home',
      homeError: reason,
    });
    navigate('/');
  });

  socket.on('session:invalid', () => {
    saveSession(null);
    set({
      session: null,
      lobby: null,
      game: null,
      solitaire: null,
      freecell: null,
      spider: null,
      noughts: null,
      minesweeper: null,
      screen: 'home',
    });
    navigate('/');
  });
}

const TOAST_TONE: Record<EventTone, Toast['tone']> = {
  plain: 'info',
  note: 'info',
  good: 'success',
  bad: 'warn',
};

/**
 * Narrates events into the running log, and surfaces the notable ones as
 * toasts. Both read from the same `narrate` call so the wording always matches.
 */
/**
 * The table's noises. Kept to what a real table makes — cards landing, cards
 * coming to you, and a flourish when someone goes out. The your-turn chime is
 * not here: it belongs to the view, not the event stream, or it would fire
 * again on every reconnect that replays events.
 */
function playEventSounds(events: GameEvent[], me: string | null): void {
  for (const event of events) {
    if (event.type === 'cardsPlayed') playCardSound();
    else if (event.type === 'cardsDrawn' && event.playerId === me) pickUpSound(event.count);
    else if (event.type === 'gameOver') fanfare();
  }
}

function announce(events: GameEvent[], get: () => AppState, set: SetState): void {
  const state = get();
  const view = state.game;
  const me = state.session?.playerId ?? null;

  const narrator = {
    name: (id: string) => {
      if (id === me) return 'You';
      return view?.players.find((p) => p.id === id)?.name ?? 'Someone';
    },
  };

  if (state.sound) playEventSounds(events, me);

  const entries: LogEntry[] = [];
  for (const event of events) {
    const narrated = narrate(event, narrator, me);
    if (!narrated) continue;

    entries.push({
      id: ++logSeq,
      text: narrated.text,
      tone: narrated.tone,
      turn: view?.turnNumber ?? 0,
    });

    if (narrated.notable && !state.logPanelOpen) {
      state.pushToast(narrated.text, TOAST_TONE[narrated.tone]);
    }
  }

  if (entries.length > 0) {
    set((current) => ({ log: [...current.log.slice(-199), ...entries] }));
  }
}

/**
 * The same, for a patience game — no opponents, so nothing to name.
 *
 * The narrator is passed in rather than branched on inside: Solitaire and
 * FreeCell emit different events but read out identically, and the only thing
 * that differs between them is which function turns an event into a sentence.
 */
function announceSolo<E>(
  events: E[],
  narrator: (event: E) => NarratedEvent | null,
  get: () => AppState,
  set: SetState,
): void {
  const state = get();
  const entries: LogEntry[] = [];

  for (const event of events) {
    const narrated = narrator(event);
    if (!narrated) continue;

    entries.push({
      id: ++logSeq,
      text: narrated.text,
      tone: narrated.tone,
      turn: state.solitaire?.moveNumber ?? state.freecell?.moveNumber ?? state.spider?.moveNumber ?? 0,
    });

    if (narrated.notable && !state.logPanelOpen) {
      state.pushToast(narrated.text, TOAST_TONE[narrated.tone]);
    }
  }

  if (entries.length > 0) {
    set((current) => ({ log: [...current.log.slice(-199), ...entries] }));
  }
}

/**
 * And for noughts and crosses — an opponent, but no cards.
 *
 * Sits between the other two: it needs a narrator that can name the other
 * player, the way Switch's does, but it has no card sounds to play and no hand
 * to read a turn number off. What it does have is the one thing neither of the
 * others needed — an ending that has to be read out, because a misère loss and
 * a win look identical on the board.
 */
function announceNoughts(events: NoughtsGameEvent[], get: () => AppState, set: SetState): void {
  const state = get();
  const view = state.noughts;
  const me = state.session?.playerId ?? null;

  const narrator = {
    name: (id: string) => {
      if (id === me) return 'You';
      return view?.players.find((p) => p.id === id)?.name ?? 'Someone';
    },
  };

  const entries: LogEntry[] = [];
  for (const event of events) {
    const narrated = narrateNoughts(event, narrator, me);
    if (!narrated) continue;

    entries.push({
      id: ++logSeq,
      text: narrated.text,
      tone: narrated.tone,
      turn: view?.turnNumber ?? 0,
    });

    if (narrated.notable && !state.logPanelOpen) {
      state.pushToast(narrated.text, TOAST_TONE[narrated.tone]);
    }
  }

  if (state.sound && events.some((e) => e.type === 'gameWon' || e.type === 'gameDrawn')) fanfare();

  if (entries.length > 0) {
    set((current) => ({ log: [...current.log.slice(-199), ...entries] }));
  }
}

/**
 * And for Minesweeper — no opponent, no cards, and a board that says most of it
 * for itself.
 *
 * Nearly everything here is deliberately silent. `announceSolo` narrates every
 * card that moves because a patience board is slow enough for that to read as
 * commentary; a Minesweeper board takes several actions a second and a line per
 * square would be a wall of noise on top of a grid the player is already
 * staring at. What is left is the three things the grid cannot tell you:
 * a flag running you out, and the two ways it ends.
 */
function announceMinesweeper(events: MinesweeperGameEvent[], get: () => AppState, set: SetState): void {
  const state = get();
  const entries: LogEntry[] = [];

  for (const event of events) {
    const narrated = narrateMinesweeper(event);
    if (!narrated) continue;

    entries.push({
      id: ++logSeq,
      text: narrated.text,
      tone: narrated.tone,
      turn: state.minesweeper?.moveNumber ?? 0,
    });

    if (narrated.notable && !state.logPanelOpen) {
      state.pushToast(narrated.text, TOAST_TONE[narrated.tone]);
    }
  }

  if (state.sound && events.some((e) => e.type === 'gameWon')) fanfare();

  if (entries.length > 0) {
    set((current) => ({ log: [...current.log.slice(-199), ...entries] }));
  }
}
